import React, { useState, useEffect } from 'react';
import { Modal } from '../ui';
import { FieldDefinition, SectionConfig } from '../../types/schema';
import { COMPARE_MODES } from '../../lib/compareModes';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (fieldName: string, fieldDef: FieldDefinition) => void;
  existingFields: string[];
  defaultSection: string;
  sections: Record<string, SectionConfig>;
}

// PROD1.7.5 — casse préservée (préparation export Mirador). Accepte majuscules
// et chiffres, exige une lettre en première position. Aligné sur la regex
// backend (SchemaService._VALID_FIELD_NAME_RE et GroundTruthService._VALID_COL_RE).
const FIELD_NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/;

export const FieldFormModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  existingFields,
  defaultSection,
  sections,
}) => {
  const [fieldName, setFieldName] = useState('');
  const [labelFr, setLabelFr] = useState('');
  const [section, setSection] = useState(defaultSection);
  const [type, setType] = useState<'string' | 'number'>('string');
  const [required, setRequired] = useState(false);
  const [compareMode, setCompareMode] = useState<'exact' | 'normalized' | 'date' | 'numeric_tolerance' | 'list' | 'none'>('exact');
  const [fieldNameError, setFieldNameError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFieldName('');
      setLabelFr('');
      setSection(defaultSection);
      setType('string');
      setRequired(false);
      setCompareMode('exact');
      setFieldNameError('');
    }
  }, [isOpen, defaultSection]);

  const validateFieldName = (value: string): string => {
    if (!value.trim()) return 'Le nom du champ est obligatoire';
    if (!FIELD_NAME_REGEX.test(value)) return 'Lettres, chiffres et underscores ; doit commencer par une lettre';
    if (existingFields.includes(value)) return 'Ce nom de champ existe déjà';
    return '';
  };

  const handleFieldNameChange = (value: string) => {
    setFieldName(value);
    setFieldNameError(validateFieldName(value));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateFieldName(fieldName);
    if (error) {
      setFieldNameError(error);
      return;
    }
    if (!labelFr.trim()) return;

    const inputType = type === 'number' ? 'number' : 'text';

    const fieldDef: FieldDefinition = {
      type,
      required,
      source_type: 'LLM',
      ui: {
        label_fr: labelFr,
        section,
        display_order: existingFields.length * 10,
        input_type: inputType,
        placeholder: '',
        help_text: '',
      },
      scoring: {
        weight: 2,
        compare_mode: compareMode as FieldDefinition['scoring']['compare_mode'],
      },
      llm: {
        description: labelFr,
        format: type === 'number' ? 'nombre' : 'texte',
        search_terms: [],
        examples_valid: [],
        examples_invalid: [],
        allowed_values: null,
        notes: null,
      },
    };

    onSubmit(fieldName, fieldDef);
    onClose();
  };

  const sortedSections = Object.entries(sections).sort((a, b) => a[1].order - b[1].order);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Nouveau champ" size="sm">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom du champ (snake_case)
            </label>
            <input
              type="text"
              value={fieldName}
              onChange={(e) => handleFieldNameChange(e.target.value)}
              className={`w-full border rounded px-3 py-2 font-mono text-sm ${fieldNameError ? 'border-red-500' : ''}`}
              placeholder="ex: date_signature"
              autoFocus
            />
            {fieldNameError && (
              <p className="text-xs text-red-600 mt-1">{fieldNameError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label (FR)
            </label>
            <input
              type="text"
              value={labelFr}
              onChange={(e) => setLabelFr(e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="ex: Date de signature"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section
            </label>
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {sortedSections.map(([key, sec]) => (
                <option key={key} value={key}>{sec.label_fr}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'string' | 'number')}
              className="w-full border rounded px-3 py-2"
            >
              <option value="string">String</option>
              <option value="number">Number</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="required"
              checked={required}
              onChange={(e) => setRequired(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="required" className="text-sm font-medium text-gray-700">
              Obligatoire
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode de comparaison
            </label>
            <select
              value={compareMode}
              onChange={(e) => setCompareMode(e.target.value as typeof compareMode)}
              className="w-full border rounded px-3 py-2"
            >
              {COMPARE_MODES.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!!fieldNameError || !fieldName.trim() || !labelFr.trim()}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
          >
            Créer
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default FieldFormModal;
